import sentryFetch from '@amzn/sentry-fetch';
import {isDev, isMockEnabled, API_RESULT_SUCCESS, isSuccessResult} from 'src/app/constant/Constants';
import {AttachmentEntry} from 'src/app/model/lighthouse/Common';
import {resolveMimeType, formatMaxSizeMb, ATTACHMENT_CONFIG} from 'src/app/config/AttachmentConfig';
import {SubmitDecisionRequest} from 'src/app/api/model/request/SubmitDecisionRequest';
import {UpdateReviewStatusRequest} from 'src/app/api/model/request/UpdateReviewStatusRequest';
import {UpdateReviewStatusResponse} from 'src/app/api/model/response/UpdateReviewStatusResponse';
import {MAX_COMMENT_LENGTH} from 'src/app/api/model/request/AddCommentRequest';
import {LighthouseReviewData, LighthouseReviewResponse} from 'src/app/api/model/response/LighthouseReviewResponse';
import {SubmitDecisionResponse} from 'src/app/api/model/response/SubmitDecisionResponse';
import {AddCommentResponse} from 'src/app/api/model/response/AddCommentResponse';
import {SearchCaseRequest} from 'src/app/api/model/request/SearchCaseRequest';
import {SearchCaseEnvelope, SearchCaseResponse} from 'src/app/api/model/response/SearchCaseResponse';
import {BulkSubmitDecisionRequest} from 'src/app/api/model/request/BulkSubmitDecisionRequest';
import {BulkSubmitDecisionResponse} from 'src/app/api/model/response/BulkSubmitDecisionResponse';
import {InitiateAttachmentUploadRequest, ConfirmAttachmentUploadRequest} from 'src/app/api/model/request/AttachmentRequest';
import {InitiateAttachmentUploadResponse, ConfirmAttachmentUploadResponse, GetAttachmentDownloadUrlResponse} from 'src/app/api/model/response/AttachmentResponse';
import {mockSearchCases} from 'src/app/mock/MockSearchApi';
import {mockFilterOptions} from 'src/app/mock/MockFilterOptions';
import {FilterOptionsEnvelope, FilterOptionsPayload} from 'src/app/api/model/response/FilterOptionsResponse';
import {
    MOCK_LIGHTHOUSE_REVIEW_DATA,
    MOCK_LIGHTHOUSE_ODQ_REVIEW_DATA,
    MOCK_LIGHTHOUSE_RELIABILITY_REVIEW_DATA
} from 'src/app/mock/lighthouse/MockReviewData';
import {MOCK_CASE_LIST} from 'src/app/mock/MockCaseList';
import {ReviewStatus} from 'src/app/constant/Enums';
import UserInfoStore from 'src/app/store/UserInfoStore';
import {logger} from 'src/app/util/Logger';
import {recordAPICall, recordAPIError, recordAPISuccess} from 'src/app/util/Metrics';
import {REGION, STAGE} from 'src/app/config/EnvConfig';
import {convertAwsRegion, EEP_API_URL_MAP, LIGHTHOUSE_SEARCH_PATH, LIGHTHOUSE_FILTER_OPTIONS_PATH, LIGHTHOUSE_BULK_SUBMIT_DECISION_PATH} from 'src/app/config/ApiConfig';

/**
 * Returns the API base URL for the current stage and region.
 * Uses the centralized EEP_API_URL_MAP from ApiConfig.
 * Returns empty string in local/test environments where STAGE is an unsubstituted template.
 */
const getApiEndpoint = (): string => {
    const stage = (STAGE as string).toLowerCase();
    const region = convertAwsRegion(REGION as string);
    const endpoint = EEP_API_URL_MAP[stage]?.[region]?.endpoint;
    if (!endpoint) {
        if (stage.includes('{{')) {
            return '';
        }
        throw new Error(`No API endpoint configured for stage=${stage}, region=${region}`);
    }
    return endpoint;
};

const API_BASE = getApiEndpoint();

/**
 * Core fetch wrapper using sentryFetch for ALI Midway CORS authentication.
 * sentryFetch handles the 5-step Midway CORS auth flow automatically
 * (redirect detection, cookie refresh, request replay).
 *
 * On non-2xx responses, attempts to parse the error body for a user-friendly message.
 * Backend returns either:
 *   - Coral exception JSON: {"__type": "ExceptionName", "message": "..."}
 *   - EEPL envelope: {"result": "ERROR", "msg": "..."}
 * The extracted message is included in the thrown Error for UI display.
 */
const apiFetch = async (method: string, path: string, body?: any): Promise<any> => {
    const url = `${API_BASE}${path}`;
    const response = await sentryFetch(url, {
        method,
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
        body: body ? JSON.stringify(body) : undefined
    });
    if (!response.ok) {
        let errorMessage = `API ${method} ${path} failed: ${response.status}`;
        try {
            const errorBody = await response.json();
            // Coral exception format: {__type, message}
            // EEPL envelope format: {result, msg}
            const backendMsg = errorBody?.message || errorBody?.msg;
            if (backendMsg) {
                errorMessage = backendMsg;
            }
        } catch {
            // Body not parseable — use the generic status message
        }
        throw new Error(errorMessage);
    }
    return response.json();
};

const OPERATION = {
    GET_LIGHTHOUSE_REVIEW: 'GetLighthouseReview',
    SUBMIT_LIGHTHOUSE_DECISION: 'SubmitLighthouseDecision',
    SEARCH_LIGHTHOUSE_CASES: 'SearchLighthouseCases',
    GET_FILTER_OPTIONS: 'GetCaseFilterOptions',
    BULK_SUBMIT_DECISION: 'BulkSubmitDecision',
    UPDATE_REVIEW_STATUS: 'UpdateReviewStatus',
    INITIATE_ATTACHMENT_UPLOAD: 'InitiateAttachmentUpload',
    CONFIRM_ATTACHMENT_UPLOAD: 'ConfirmAttachmentUpload',
    GET_ATTACHMENT_DOWNLOAD_URL: 'GetAttachmentDownloadUrl',
    ADD_COMMENT: 'AddComment'
} as const;

export namespace LighthouseApi {

    const logRequest = (operation: string, request: any) => {
        logger.info(`[${operation}] Request initiated`, {
            operation,
            userId: UserInfoStore.value,
            requestData: request,
            timestamp: new Date().toISOString()
        });
        recordAPICall(operation);
    };

    const logResponse = (operation: string, duration: number) => {
        logger.info(`[${operation}] Response received`, {
            operation,
            userId: UserInfoStore.value,
            duration,
            timestamp: new Date().toISOString()
        });
        recordAPISuccess(operation, duration);
    };

    const logError = (operation: string, error: any, request?: any) => {
        logger.error(`[${operation}] Error occurred`, {
            operation,
            error: error.message || error,
            errorStack: error.stack,
            userId: UserInfoStore.value,
            requestData: request,
            timestamp: new Date().toISOString()
        });
        recordAPIError(operation);
    };

    /**
     * Fetches a Lighthouse review by ID.
     * In dev mode, returns mock data after a simulated delay.
     */
    export const getReview = async (reviewId: string): Promise<LighthouseReviewResponse> => {
        const startTime = Date.now();
        try {
            logRequest(OPERATION.GET_LIGHTHOUSE_REVIEW, {reviewId});

            // Local-dev shortcut for the ODQ / Reliability templates: open
            // /lighthouse-review?reviewId=lh-odq (or lh-rel) on the dev server. Mirrors how
            // EEPApi.getReview keys its mocks off the reviewId. isDev() is true only when
            // IS_LOCAL_DEV is injected, so this cannot fire on a deployed stage.
            if (isDev()) {
                if (reviewId.includes('lh-odq')) {
                    return {result: API_RESULT_SUCCESS, data: MOCK_LIGHTHOUSE_ODQ_REVIEW_DATA};
                }
                if (reviewId.includes('lh-rel')) {
                    return {result: API_RESULT_SUCCESS, data: MOCK_LIGHTHOUSE_RELIABILITY_REVIEW_DATA};
                }
            }

            if (isMockEnabled()) {
                await new Promise(resolve => setTimeout(resolve, 500));
                // Align the mock detail with the case row that was clicked (if it is one of the
                // MOCK_CASE_LIST rows) so status-dependent flows — like Reopen, which requires
                // COMPLETED + NO_ACTION — behave the same as against the real backend.
                const row = MOCK_CASE_LIST.find(c => c.caseId === reviewId);
                const data: LighthouseReviewData = row
                    ? {
                        ...MOCK_LIGHTHOUSE_REVIEW_DATA,
                        reviewId: row.caseId,
                        referenceId: row.referenceId,
                        caseType: row.caseType ?? MOCK_LIGHTHOUSE_REVIEW_DATA.caseType,
                        caseSubType: row.caseSubType ?? MOCK_LIGHTHOUSE_REVIEW_DATA.caseSubType,
                        country: row.country ?? MOCK_LIGHTHOUSE_REVIEW_DATA.country,
                        reviewStatus: row.status as ReviewStatus,
                        reviewResult: row.decision ?? ''
                    }
                    : MOCK_LIGHTHOUSE_REVIEW_DATA;
                return {result: API_RESULT_SUCCESS, data};
            }

            const path = `/lighthouse/reviews/${reviewId}`;
            const response = await apiFetch('GET', path);
            logResponse(OPERATION.GET_LIGHTHOUSE_REVIEW, Date.now() - startTime);
            return response;
        } catch (error) {
            logError(OPERATION.GET_LIGHTHOUSE_REVIEW, error, {reviewId});
            throw error;
        }
    };

    /**
     * Submits a decision for a Lighthouse review.
     */
    export const submitDecision = async (request: SubmitDecisionRequest): Promise<SubmitDecisionResponse> => {
        const startTime = Date.now();
        try {
            logRequest(OPERATION.SUBMIT_LIGHTHOUSE_DECISION, request);

            if (isMockEnabled()) {
                await new Promise(resolve => setTimeout(resolve, 300));
                return {result: API_RESULT_SUCCESS, msg: 'Decision submitted successfully'};
            }

            const path = `/lighthouse/reviews/${request.reviewId}/decision`;
            const response = await apiFetch('POST', path, request);
            logResponse(OPERATION.SUBMIT_LIGHTHOUSE_DECISION, Date.now() - startTime);
            return response;
        } catch (error) {
            logError(OPERATION.SUBMIT_LIGHTHOUSE_DECISION, error, request);
            throw error;
        }
    };

    /**
     * Pauses or resumes a review by updating only its status (PENDING<->PAUSED).
     * Does NOT trigger the enforcement (EWM) flow — the backend only changes the status
     * and lets the DDB Streams pipeline create/remove the Mission Control task.
     * The backend enforces the allowed transition (PENDING->PAUSED, PAUSED->PENDING).
     * POST /lighthouse/reviews/{reviewId}/status
     */
    export const updateReviewStatus = async (request: UpdateReviewStatusRequest): Promise<UpdateReviewStatusResponse> => {
        const startTime = Date.now();
        try {
            logRequest(OPERATION.UPDATE_REVIEW_STATUS, request);

            if (isMockEnabled()) {
                await new Promise(resolve => setTimeout(resolve, 300));
                return {result: API_RESULT_SUCCESS, msg: `Review status updated to ${request.targetStatus}`};
            }

            const path = `/lighthouse/reviews/${request.reviewId}/status`;
            const response = await apiFetch('POST', path, request);
            logResponse(OPERATION.UPDATE_REVIEW_STATUS, Date.now() - startTime);
            return response;
        } catch (error) {
            logError(OPERATION.UPDATE_REVIEW_STATUS, error, request);
            throw error;
        }
    };

    /**
     * Adds a free-text comment to a review's audit trail. The review must be PENDING
     * (the backend rejects otherwise). The comment is trimmed and validated
     * client-side (non-blank, <= MAX_COMMENT_LENGTH) before the call; the backend
     * re-validates and remains the source of truth.
     *
     * The comment text is intentionally omitted from request logging (free-text).
     * POST /lighthouse/reviews/{reviewId}/comment
     */
    export const addComment = async (reviewId: string, comment: string): Promise<AddCommentResponse> => {
        const startTime = Date.now();
        const trimmed = comment.trim();
        if (trimmed.length === 0) {
            throw new Error('Comment must not be empty');
        }
        if (trimmed.length > MAX_COMMENT_LENGTH) {
            throw new Error(`Comment exceeds the maximum length of ${MAX_COMMENT_LENGTH} characters`);
        }
        try {
            logRequest(OPERATION.ADD_COMMENT, {reviewId});

            if (isMockEnabled()) {
                await new Promise(resolve => setTimeout(resolve, 200));
                return {result: API_RESULT_SUCCESS, msg: 'Comment added successfully!'};
            }

            const path = `/lighthouse/reviews/${reviewId}/comment`;
            const response = await apiFetch('POST', path, {comment: trimmed});
            logResponse(OPERATION.ADD_COMMENT, Date.now() - startTime);
            return response;
        } catch (error) {
            logError(OPERATION.ADD_COMMENT, error, {reviewId});
            throw error;
        }
    };

    /**
     * Case Audit (Management) search — POST /lighthouse/cases/search. Serves local mock
     * data while isMockEnabled() is on (USE_MOCK_DATA flag, hard-gated off in prod);
     * otherwise calls the backend. Both paths return the same result/msg/data envelope,
     * which is validated (lowercase "success") and unwrapped here, so flipping the flag
     * needs no call-site changes.
     */
    export const searchCases = async (request: SearchCaseRequest): Promise<SearchCaseResponse> => {
        const startTime = Date.now();
        try {
            logRequest(OPERATION.SEARCH_LIGHTHOUSE_CASES, request);

            const response: SearchCaseEnvelope = isMockEnabled()
                ? await mockSearchCases(request)
                : await apiFetch('POST', LIGHTHOUSE_SEARCH_PATH, request);
            if (!isSuccessResult(response.result)) {
                throw new Error(response.msg || 'Search cases failed');
            }
            logResponse(OPERATION.SEARCH_LIGHTHOUSE_CASES, Date.now() - startTime);
            return response.data;
        } catch (error) {
            logError(OPERATION.SEARCH_LIGHTHOUSE_CASES, error, request);
            throw error;
        }
    };

    /**
     * Distinct Case Audit filter values (raw index contents) — POST /lighthouse/cases/filter-options.
     * Same mock-branch/envelope-unwrap pattern as searchCases; call sites receive the payload only.
     * Callers are expected to fall back to the static DropDownConfig lists when this throws.
     */
    export const getFilterOptions = async (): Promise<FilterOptionsPayload> => {
        const startTime = Date.now();
        try {
            logRequest(OPERATION.GET_FILTER_OPTIONS, {});

            const response: FilterOptionsEnvelope = isMockEnabled()
                ? await mockFilterOptions()
                : await apiFetch('POST', LIGHTHOUSE_FILTER_OPTIONS_PATH, {});
            if (!isSuccessResult(response.result)) {
                throw new Error(response.msg || 'Get filter options failed');
            }
            logResponse(OPERATION.GET_FILTER_OPTIONS, Date.now() - startTime);
            return response.data;
        } catch (error) {
            logError(OPERATION.GET_FILTER_OPTIONS, error);
            throw error;
        }
    };

    /**
     * Bulk NO_ACTION decision for managers. Submits up to 1000 reviews
     * for bulk closure with a shared notes justification.
     * POST /reviews/bulk-submit-decision
     */
    export const bulkSubmitDecision = async (request: BulkSubmitDecisionRequest): Promise<BulkSubmitDecisionResponse> => {
        const startTime = Date.now();
        try {
            logRequest(OPERATION.BULK_SUBMIT_DECISION, request);

            if (isDev()) {
                await new Promise(resolve => setTimeout(resolve, 500));
                return {result: 'SUCCESS', totalProcessed: request.reviewIds.length, totalFailed: 0};
            }

            const response = await apiFetch('POST', LIGHTHOUSE_BULK_SUBMIT_DECISION_PATH, request);
            logResponse(OPERATION.BULK_SUBMIT_DECISION, Date.now() - startTime);
            return response;
        } catch (error) {
            logError(OPERATION.BULK_SUBMIT_DECISION, error, request);
            throw error;
        }
    };

    // ──────────────────────────────────────────────────────────────────────────
    // Attachment APIs
    // ──────────────────────────────────────────────────────────────────────────

    /**
     * Initiates an attachment upload by requesting a pre-signed S3 PUT URL (valid 60 min).
     * The browser must PUT the file directly to the returned presignedUrl with the exact
     * Content-Type declared in mimeType. After a successful PUT, call confirmUpload().
     *
     * POST /lighthouse/reviews/{reviewId}/attachment/initiate
     */
    export const initiateUpload = async (request: InitiateAttachmentUploadRequest): Promise<InitiateAttachmentUploadResponse> => {
        const startTime = Date.now();
        try {
            logRequest(OPERATION.INITIATE_ATTACHMENT_UPLOAD, request);

            if (isMockEnabled()) {
                await new Promise(resolve => setTimeout(resolve, 200));
                return {
                    result: API_RESULT_SUCCESS,
                    msg: 'Upload initiated',
                    data: {
                        presignedUrl: 'https://s3.amazonaws.com/mock-bucket/mock-upload-url',
                        s3Key: `${request.reviewId}/${Date.now()}_${request.fileName}`,
                        expiresIn: 3600
                    }
                };
            }

            const path = `/lighthouse/reviews/${request.reviewId}/attachment/initiate`;
            const response = await apiFetch('POST', path, {
                fileName: request.fileName,
                fileSize: request.fileSize,
                mimeType: request.mimeType
            });
            logResponse(OPERATION.INITIATE_ATTACHMENT_UPLOAD, Date.now() - startTime);
            return response;
        } catch (error) {
            logError(OPERATION.INITIATE_ATTACHMENT_UPLOAD, error, request);
            throw error;
        }
    };

    /**
     * Confirms an attachment upload after the browser successfully PUT the file to S3.
     * The backend verifies the file exists (HeadObject), checks size match, and persists
     * metadata. Returns a fileId used for future download URL requests.
     *
     * POST /lighthouse/reviews/{reviewId}/attachment/confirm
     */
    export const confirmUpload = async (request: ConfirmAttachmentUploadRequest): Promise<ConfirmAttachmentUploadResponse> => {
        const startTime = Date.now();
        try {
            logRequest(OPERATION.CONFIRM_ATTACHMENT_UPLOAD, request);

            if (isMockEnabled()) {
                await new Promise(resolve => setTimeout(resolve, 200));
                return {
                    result: API_RESULT_SUCCESS,
                    msg: 'Upload confirmed',
                    data: {fileId: `mock-file-id-${Date.now()}`}
                };
            }

            const path = `/lighthouse/reviews/${request.reviewId}/attachment/confirm`;
            const response = await apiFetch('POST', path, {
                s3Key: request.s3Key,
                fileName: request.fileName,
                fileSize: request.fileSize,
                mimeType: request.mimeType
            });
            logResponse(OPERATION.CONFIRM_ATTACHMENT_UPLOAD, Date.now() - startTime);
            return response;
        } catch (error) {
            logError(OPERATION.CONFIRM_ATTACHMENT_UPLOAD, error, request);
            throw error;
        }
    };

    /**
     * Gets a fresh pre-signed S3 GET URL (valid 15 min) for downloading an attachment.
     * content-disposition: attachment is set server-side to force browser download.
     *
     * GET /lighthouse/reviews/{reviewId}/attachment/{fileId}/url
     */
    export const getDownloadUrl = async (reviewId: string, fileId: string): Promise<GetAttachmentDownloadUrlResponse> => {
        const startTime = Date.now();
        try {
            logRequest(OPERATION.GET_ATTACHMENT_DOWNLOAD_URL, {reviewId, fileId});

            if (isMockEnabled()) {
                await new Promise(resolve => setTimeout(resolve, 200));
                return {
                    result: API_RESULT_SUCCESS,
                    msg: 'Download URL generated',
                    data: {
                        downloadUrl: 'https://s3.amazonaws.com/mock-bucket/mock-download-url',
                        fileName: 'mock-file.pdf',
                        expiresIn: 900
                    }
                };
            }

            const path = `/lighthouse/reviews/${reviewId}/attachment/${encodeURIComponent(fileId)}/url`;
            const response = await apiFetch('GET', path);
            logResponse(OPERATION.GET_ATTACHMENT_DOWNLOAD_URL, Date.now() - startTime);
            return response;
        } catch (error) {
            logError(OPERATION.GET_ATTACHMENT_DOWNLOAD_URL, error, {reviewId, fileId});
            throw error;
        }
    };

    /**
     * Uploads the file bytes directly to S3 via the pre-signed PUT URL.
     * Uses the browser's native fetch (NOT sentryFetch) — S3 is a third-party
     * origin with no Midway/CORS auth, and the URL is already pre-signed.
     * The Content-Type MUST match the mimeType sent to initiateUpload.
     */
    const uploadToS3 = async (presignedUrl: string, file: File, mimeType: string): Promise<void> => {
        if (isMockEnabled()) {
            await new Promise(resolve => setTimeout(resolve, 200));
            return;
        }
        const response = await fetch(presignedUrl, {
            method: 'PUT',
            body: file,
            headers: {'Content-Type': mimeType},
        });
        if (!response.ok) {
            throw new Error(`File upload to S3 failed for [${file.name}]: ${response.status}`);
        }
    };

    /**
     * Full single-file attachment upload: initiate → PUT to S3 → confirm.
     * Returns a fully-populated {@link AttachmentEntry} (using the fileId from the
     * confirm response and the authenticated uploader alias) so callers can
     * optimistically append it to local state WITHOUT re-fetching the review.
     *
     * Throws on any failure (initiate, PUT, or confirm) so the caller can surface
     * the error and avoid recording a partial result. Reusable by any page that
     * has a reviewId.
     */
    export const uploadAttachment = async (reviewId: string, file: File): Promise<AttachmentEntry> => {
        // Belt-and-suspenders client-side validation. KatFileUpload already gates
        // selection, but this protects programmatic callers, future picker swaps, and
        // drag-drop paths. The backend remains the source of truth.
        const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
        if (!(ATTACHMENT_CONFIG.ALLOWED_EXTENSIONS as readonly string[]).includes(ext)) {
            throw new Error(`Unsupported file type: .${ext}`);
        }
        if (file.name.length > ATTACHMENT_CONFIG.MAX_FILE_NAME_LENGTH) {
            throw new Error(`File name exceeds the maximum length of ${ATTACHMENT_CONFIG.MAX_FILE_NAME_LENGTH} characters`);
        }
        if (file.size > ATTACHMENT_CONFIG.MAX_FILE_SIZE_BYTES) {
            throw new Error(`File exceeds the maximum size of ${formatMaxSizeMb(ATTACHMENT_CONFIG.MAX_FILE_SIZE_BYTES)} MB`);
        }

        const mimeType = resolveMimeType(file.name, file.type);

        const initRes = await initiateUpload({reviewId, fileName: file.name, fileSize: file.size, mimeType});
        if (!isSuccessResult(initRes.result)) {
            throw new Error(initRes.msg || `Failed to initiate upload for [${file.name}]`);
        }

        await uploadToS3(initRes.data.presignedUrl, file, mimeType);

        const confirmRes = await confirmUpload({
            reviewId,
            s3Key: initRes.data.s3Key,
            fileName: file.name,
            fileSize: file.size,
            mimeType,
        });
        if (!isSuccessResult(confirmRes.result)) {
            throw new Error(confirmRes.msg || `Failed to confirm upload for [${file.name}]`);
        }

        // The backend fileId is the record's creationDate as an ISO-8601 string,
        // so it doubles as the authoritative upload timestamp. Fall back to the
        // local clock only if it isn't a parseable date.
        const parsedUploadedAt = Date.parse(confirmRes.data.fileId);

        return {
            fileId: confirmRes.data.fileId,
            fileName: file.name,
            fileSize: file.size,
            uploadedBy: UserInfoStore.value,
            uploadedAt: Number.isNaN(parsedUploadedAt) ? Date.now() : parsedUploadedAt,
        };
    };

    /**
     * Fetches a fresh pre-signed download URL and triggers a browser download.
     * The backend stamps content-disposition: attachment, so this downloads the
     * file for all types (no inline preview). Reusable download helper.
     */
    export const triggerAttachmentDownload = async (reviewId: string, fileId: string): Promise<void> => {
        const response = await getDownloadUrl(reviewId, fileId);
        if (!isSuccessResult(response.result)) {
            throw new Error(response.msg || 'Failed to generate download URL');
        }
        const anchor = document.createElement('a');
        anchor.href = response.data.downloadUrl;
        anchor.rel = 'noopener noreferrer';
        // fileName is a best-effort hint; S3's content-disposition governs the saved name.
        anchor.download = response.data.fileName ?? '';
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
    };

}
